import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Wizard,
  Modal,
  Box,
  Button,
  SpaceBetween,
  Alert,
} from '@cloudscape-design/components';
import { Question, MediaIdsMap } from 'src/types/helios/Question';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { QuestionFormValidator } from 'src/validations/QuestionFormValidator';
import { useTags } from 'src/store/tags/useTags';
import useUserProfile from 'src/hooks/useUserProfile';
import { useLocations } from 'src/store/locations/useLocations';
import { useCategories } from 'src/store/categories/useCategories';
import { useDivision } from 'src/store/division/useDivision';
import { useQuestion } from 'src/store/question/useQuestion';
import { useMediaUpload } from 'src/hooks/useMediaUpload';
import { LANGUAGE_LIST, EntityType } from 'src/contstant/commonLabels';
import { SelectProps } from '@cloudscape-design/components';
import {
  extractBase64Files,
  extractSrcValues,
  replaceImagesWithMediaIds,
  replacePresignedUrlsWithMediaIdsForQuestion,
  replacePresignedUrlsWithMediaIdsForSubQuestion,
  convertFilesToFileData,
  mergeMediaIdsFromDocuments,
  migrateSubQuestionMediaIds,
  replaceMediaIdsWithPresignedUrlsInSubQuestions,
} from 'src/utils/mediaUtils';
import { getSubQuestionFieldKey } from 'src/helper/questionHelper';
import { ApiRequestStatus } from 'src/types/ApiRequestStatus';
import ApiStatusFlashBar from '../ApiStatusFlashBar';
import UploadProgressIndicator from '../UploadProgressIndicator';
import { FileData } from 'src/components/textEditor/TextEditor';
import { FileMetadata } from 'src/components/FileDisplay';
import { useSplitPanel } from 'src/context/SplitPanelContext';

// Import step components
import BasicInformation from './steps/BasicInformation';
import AuditCriteria from './steps/AuditCriteria';
import SubQuestions from './steps/SubQuestions';
import Documentation from './steps/Documentation';
import {
  openQuestionDetailsPage,
  loadExistingDocuments,
} from 'src/helper/questionHelper';
import { useNavigate } from 'react-router-dom';

/**
 * Props interface for QuestionFormWizard component
 */
interface QuestionFormWizardProps {
  /** The question object to edit, null for new questions */
  question?: Question | null;
  /** Flag indicating if this is a copy operation */
  isCopyQuestion?: boolean;
  /** Callback function called when form is cancelled */
  onCancel?: () => void;
}

/**
 * Form data structure for question details
 */
export interface QuestionFormParams {
  questionId: string;
  version?: number;
  isLatestVersion?: boolean;
  divisionName: string;
  businessUnit: string;
  program?: string;
  categories?: string[];
  auditScore: string;
  isActive: boolean;
  defaultLanguage: SelectProps.Option;
  title: string;
  questionText: string;
  auditInstruction: string;
  passCriteria: string;
  failCriteria: string;
  justification: string;
  taskName: string;
  taskDescription: string;
  taskDurationDays?: number;
  translationStatus?: string;
  docLinks: Array<{
    title?: string;
    link?: string;
  }>;
  locationsExcluded: string[];
  tagsIncluded: Record<string, string[]>;
  tagsExcluded?: Record<string, string[]>;
  subQuestions?: any[];
  createdAt?: number;
  createdBy?: string;
  lastUpdatedAt?: number;
  lastUpdatedBy?: string;
  changeLog?: string;
  requireCommentOnFail?: boolean;
  requireEvidenceOnFail?: boolean;
  requireCommentOnPass?: boolean;
  requireEvidenceOnPass?: boolean;
}

/**
 * QuestionFormWizard component with stepper workflow
 */
const QuestionFormWizard: React.FC<QuestionFormWizardProps> = ({
  question,
  onCancel,
  isCopyQuestion = false,
}) => {
  // Hooks
  const { getUserId, getUserBusinessUnitId, getUserProgramId } = useUserProfile();
  const { locationsList, loadLocations } = useLocations();
  const { tagsList, loadTags } = useTags();
  const { categoriesList, loadCategories } = useCategories();
  const { divisionList, loadDivisionsByBusinessUnit } = useDivision();
  const navigate = useNavigate();

  // Question store hooks
  const {
    updateQuestion,
    createQuestion,
    clearQuestionAllApiStatus,
    createQuestionApiStatus,
    updateQuestionApiStatus,
    currentSelectedQuestion,
    loadQuestionById,
  } = useQuestion();

  // Media upload hooks
  const { isUploading, uploadProgress, uploadImagesFromContent, uploadfiles } =
    useMediaUpload();

  // Split panel context
  const { closeView, content } = useSplitPanel();

  // Initialize form data
  const getInitialValues = (): QuestionFormParams => ({
    questionId: question?.questionId || '',
    version: question?.version,
    isLatestVersion: question?.isLatestVersion,
    divisionName: isCopyQuestion ? '' : question?.divisionName || '',
    businessUnit: question?.businessUnit || getUserBusinessUnitId(),
    program: question?.program || '',
    categories: question?.categories,
    defaultLanguage: LANGUAGE_LIST.find(
      (item) => item.value === question?.defaultLanguage || 'en',
    )!,
    auditScore: question?.auditScore?.toString() || '1',
    isActive: question?.isActive ?? true,
    title: question?.defaultLanguageContent?.questionDetails.title || '',
    questionText: question?.defaultLanguageContent?.questionDetails.questionText || '',
    auditInstruction:
      question?.defaultLanguageContent?.questionDetails.auditInstruction || '',
    passCriteria: question?.defaultLanguageContent?.questionDetails.passCriteria || '',
    failCriteria: question?.defaultLanguageContent?.questionDetails.failCriteria || '',
    justification: question?.defaultLanguageContent?.questionDetails.justification || '',
    taskName: question?.defaultLanguageContent?.taskDetails?.taskName || '',
    taskDescription: question?.defaultLanguageContent?.taskDetails?.taskDescription || '',
    taskDurationDays:
      question?.defaultLanguageContent?.taskDetails?.taskDurationDays || 1,
    translationStatus: question?.translationStatus,
    docLinks: question?.docLinks || [],
    tagsIncluded: question?.tagsIncluded || {},
    tagsExcluded: question?.tagsExcluded || {},
    subQuestions: question?.subQuestions?.map(migrateSubQuestionMediaIds) || [],
    // Backend stores location codes as strings, form uses strings directly
    // No transformation needed - eliminates race condition with locationsList loading
    locationsExcluded: question?.locationsExcluded || [],
    createdAt: question?.createdAt,
    createdBy: question?.createdBy,
    lastUpdatedAt: question?.lastUpdatedAt,
    lastUpdatedBy: question?.lastUpdatedBy,
    changeLog: '',
    requireCommentOnFail: question?.requireCommentOnFail ?? false,
    requireEvidenceOnFail: question?.requireEvidenceOnFail ?? false,
    requireCommentOnPass: question?.requireCommentOnPass ?? false,
    requireEvidenceOnPass: question?.requireEvidenceOnPass ?? false,
  });

  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [isSaveClicked, setSaveClicked] = useState(false);
  const [formData, setFormData] = useState<QuestionFormParams>(getInitialValues);
  const [fileList, setFileList] = useState<Record<string, FileData[]>>({});
  const [documentFiles, setDocumentFiles] = useState<Record<string, File[]>>({});
  const [documentFilesMetadata, setDocumentFilesMetadata] = useState<
    Record<string, FileMetadata[]>
  >({});
  const [validationStatus, setValidationStatus] = useState<ApiRequestStatus[]>([]);
  const [enableFileUploads] = useState(true);
  const [showEnglishConfirmationModal, setShowEnglishConfirmationModal] = useState(false);
  const initializedQuestionId = useRef<string | null>(null);

  // Form validation
  const { fieldValidationError, validate, errorALLMessage } = useFormValidation(
    formData,
    QuestionFormValidator(question, isCopyQuestion, tagsList),
  );

  // Memoize initialization logic to avoid dependency issues
  const initializeFormData = useCallback(() => {
    if (
      question &&
      question.questionId &&
      initializedQuestionId.current !== question.questionId
    ) {
      initializedQuestionId.current = question.questionId;

      // Initialize form data using the existing helper function
      const initialData = getInitialValues();

      // Replace media IDs with presigned URLs in subQuestions for display
      const updatedSubQuestions = replaceMediaIdsWithPresignedUrlsInSubQuestions(
        initialData.subQuestions || [],
      );
      setFormData({ ...initialData, subQuestions: updatedSubQuestions });

      if (question.mediaResponses) {
        // Load all document metadata (main question fields + subquestions)
        const documentMetadataMap = loadExistingDocuments(question);
        setDocumentFilesMetadata(documentMetadataMap);
      }
    }
  }, [question, getInitialValues]);

  // Load existing document files when editing a question
  useEffect(() => {
    initializeFormData();
  }, [initializeFormData]);

  useEffect(() => {
    if (errorALLMessage && isSaveClicked) {
      setValidationStatus([
        {
          isLoading: false,
          isSuccess: false,
          successMessage: null,
          errorMessage: 'Error',
          headerMessage: 'Please fix the validation errors before saving the question.',
          errorHtml: errorALLMessage,
        },
      ]);
    } else {
      setValidationStatus([]);
    }
  }, [errorALLMessage]);

  useEffect(() => {
    if (getUserBusinessUnitId()) {
      loadDivisionsByBusinessUnit({
        businessUnitId: getUserBusinessUnitId(),
        programId: getUserProgramId(),
        isDeleted: false,
        isActive: true,
      });
    }
    loadLocations({
      isEnabledForBU: true,
      businessUnitId: getUserBusinessUnitId(),
    });
    loadCategories({
      businessUnitId: getUserBusinessUnitId()!,
      isActive: true,
    });
    loadTags({
      businessUnitId: getUserBusinessUnitId()!,
      programId: getUserProgramId()!,
    });
  }, [getUserBusinessUnitId()]);

  // Handle version conflicts with refresh capability
  const handleVersionConflictRefresh = useCallback(() => {
    if (question?.questionId) {
      // Clear current errors
      clearQuestionAllApiStatus();

      // Reload the question to get the latest version
      loadQuestionById({
        questionId: question.questionId,
        programId: getUserProgramId(),
        businessUnit: getUserBusinessUnitId(),
        divisionName: question.divisionName,
      });

      // Reset form with latest data
      setCurrentStep(0);
    }
  }, [
    question,
    clearQuestionAllApiStatus,
    loadQuestionById,
    getUserProgramId,
    getUserBusinessUnitId,
  ]);

  // Handle successful save - navigate back to read-only view
  useEffect(() => {
    if (createQuestionApiStatus.isSuccess || updateQuestionApiStatus.isSuccess) {
      if (currentSelectedQuestion) {
        openQuestionDetailsPage(navigate, currentSelectedQuestion, true, false);
      } else {
        onCancel?.(); // This navigates back to read-only view
      }

      clearQuestionAllApiStatus();
    }
  }, [
    createQuestionApiStatus,
    updateQuestionApiStatus,
    onCancel,
    clearQuestionAllApiStatus,
  ]);

  // Define wizard steps
  const steps = [
    {
      title: 'Basic Information',
      content: (
        <BasicInformation
          formData={formData}
          setFormData={setFormData}
          fieldValidationError={fieldValidationError}
          divisionList={divisionList}
          categories={categoriesList}
          question={question || undefined}
          isCopyQuestion={isCopyQuestion}
          tagsList={tagsList}
          locationsList={locationsList}
        />
      ),
      isOptional: false,
    },
    {
      title: 'Audit Criteria',
      content: (
        <AuditCriteria
          formData={formData}
          setFormData={setFormData}
          fieldValidationError={fieldValidationError}
          fileList={fileList}
          setFileList={setFileList}
          documentFiles={documentFiles}
          setDocumentFiles={enableFileUploads ? setDocumentFiles : undefined}
          documentFilesMetadata={documentFilesMetadata}
          setDocumentFilesMetadata={
            enableFileUploads ? setDocumentFilesMetadata : undefined
          }
          enableFileUploads={enableFileUploads}
        />
      ),
      isOptional: false,
    },
    {
      title: 'Sub Questions',
      content: (
        <SubQuestions
          formData={formData}
          setFormData={setFormData}
          fileList={fileList}
          setFileList={setFileList}
          documentFiles={documentFiles}
          setDocumentFiles={enableFileUploads ? setDocumentFiles : undefined}
          documentFilesMetadata={documentFilesMetadata}
          setDocumentFilesMetadata={
            enableFileUploads ? setDocumentFilesMetadata : undefined
          }
        />
      ),
      isOptional: true,
    },
    {
      title: 'Documentation',
      content: (
        <Documentation
          formData={formData}
          setFormData={setFormData}
          fieldValidationError={fieldValidationError}
          question={question || undefined}
          isCopyQuestion={isCopyQuestion}
        />
      ),
      isOptional: true,
    },
  ];

  // Handle step navigation
  const handleStepChange = useCallback(
    (stepIndex: number) => {
      // Close split panel if it's currently open
      if (content) {
        closeView();
      }
      setCurrentStep(stepIndex);
    },
    [content, closeView],
  );

  // Clear validation messages
  const clearValidationMessages = () => {
    setValidationStatus([]);
  };

  /**
   * Replaces temporary image URLs with permanent media IDs in form content
   * This function processes all text fields that may contain embedded images
   * @param data - The form data containing text fields with potential image URLs
   * @param mediaIds - Mapping of temporary image URLs to permanent media IDs
   * @returns Updated form data with media IDs replacing image URLs
   */
  const replaceAllImagesWithMediaIdsOfContents = (
    data: QuestionFormParams,
    mediaIds: Record<string, string> | undefined,
  ): QuestionFormParams => {
    // Early return if no media ID mappings are provided
    if (!mediaIds) return data;

    // Create shallow copy to avoid mutating original data
    const updatedFormData = { ...data };

    // Define all text fields that may contain embedded images
    // Using 'as const' for type safety and autocompletion
    const textFields = [
      'questionText',
      'auditInstruction',
      'passCriteria',
      'failCriteria',
      'justification',
      'taskDescription',
    ] as const;

    // Process each main text field by replacing image URLs with media IDs
    // Only process fields that have content to avoid unnecessary operations
    textFields.forEach((field) => {
      if (updatedFormData[field]) {
        updatedFormData[field] = replaceImagesWithMediaIds(
          updatedFormData[field],
          mediaIds,
        );
      }
    });

    // Handle subQuestions which have nested structure and require special processing
    if (updatedFormData.subQuestions?.length) {
      updatedFormData.subQuestions = updatedFormData.subQuestions.map((subQuestion) => ({
        ...subQuestion,
        // Replace images in subQuestion instruction field
        instruction: subQuestion.instruction
          ? replaceImagesWithMediaIds(subQuestion.instruction, mediaIds)
          : subQuestion.instruction,
        // Handle nested taskDetails object if it exists
        taskDetails: subQuestion.taskDetails
          ? {
              ...subQuestion.taskDetails,
              // Replace images in taskDescription within taskDetails
              taskDescription: replaceImagesWithMediaIds(
                subQuestion.taskDetails.taskDescription,
                mediaIds,
              ),
            }
          : subQuestion.taskDetails,
      }));
    }

    return updatedFormData;
  };

  /**
   * Processes existing question data by replacing presigned URLs with media IDs
   * This function handles both main form fields and subQuestions, extracting media IDs for tracking
   *
   * IMPORTANT: This function expects subQuestions to have mediaResponses or mediaResponsesMap
   * populated by the backend when the question is loaded. If images are not rendering in
   * subQuestions, verify that the backend is returning these fields with presigned URLs.
   *
   * @param data - The form data to process
   * @returns Object containing updated form data and extracted media IDs map
   */
  const replaceAllImageUrlsWithMediaIdsOfContents = (data: QuestionFormParams) => {
    // Create shallow copy to avoid mutating original data
    const updatedFormData = { ...data };

    // Define all main text fields that may contain presigned image URLs
    const textFields = [
      'auditInstruction',
      'justification',
      'taskDescription',
      'questionText',
      'passCriteria',
      'failCriteria',
    ] as const;

    // Process main form fields if question has existing media responses
    // This replaces presigned URLs from existing question data with media IDs
    if (question?.mediaResponses) {
      textFields.forEach((field) => {
        updatedFormData[field] = replacePresignedUrlsWithMediaIdsForQuestion(
          data[field],
          question.mediaResponses,
        );
      });
    }

    // Process subQuestions which have their own media responses and nested structure
    if (updatedFormData.subQuestions?.length) {
      updatedFormData.subQuestions = updatedFormData.subQuestions.map((subQuestion) => {
        // Use mediaResponsesMap if available (new structure), fallback to mediaResponses (legacy)
        const instructionMediaResponses =
          subQuestion.mediaResponsesMap?.instruction || subQuestion.mediaResponses;
        const taskDescriptionMediaResponses =
          subQuestion.mediaResponsesMap?.taskDescription || subQuestion.mediaResponses;

        // Process each subQuestion by replacing presigned URLs with media IDs
        const processedSubQuestion = {
          ...subQuestion,
          // Replace presigned URLs in instruction using field-specific media responses
          instruction: replacePresignedUrlsWithMediaIdsForSubQuestion(
            subQuestion.instruction,
            instructionMediaResponses,
          ),
          // Handle nested taskDetails if it exists
          taskDetails: subQuestion.taskDetails && {
            ...subQuestion.taskDetails,
            // Replace presigned URLs in taskDescription using field-specific media responses
            taskDescription: replacePresignedUrlsWithMediaIdsForSubQuestion(
              subQuestion.taskDetails.taskDescription,
              taskDescriptionMediaResponses,
            ),
          },
        };

        // Note: We no longer set mediaIds here (legacy field)
        // Media IDs are now tracked via mediaIdsMap which is built later in handleSubmit
        // This provides better field-level granularity (instruction vs taskDescription)
        // Old questions with mediaIds will still be readable for backward compatibility

        // Return processed subQuestion without mediaIds
        return processedSubQuestion;
      });
    }

    // Create media IDs map for main form fields using reduce for TypeScript compatibility
    // This map tracks which media IDs are used in each field for cleanup and validation
    const mediaIdsMap: MediaIdsMap = textFields.reduce((acc, field) => {
      // Extract media IDs from each field and add to accumulator
      acc[field] = extractSrcValues(updatedFormData[field]);
      return acc;
    }, {} as MediaIdsMap);

    // Return both the processed form data and the media IDs mapping
    return { updatedFormData, mediaIdsMap };
  };

  // Perform the actual form submission (called after English confirmation)
  const performSubmit = async () => {
    const listOfImages: FileData[] = [
      ...extractBase64Files(formData.auditInstruction),
      ...extractBase64Files(formData.justification),
      ...extractBase64Files(formData.taskDescription),
      ...extractBase64Files(formData.questionText),
      ...extractBase64Files(formData.passCriteria),
      ...extractBase64Files(formData.failCriteria),
      ...(formData.subQuestions?.flatMap((item) => [
        ...extractBase64Files(item.instruction),
        ...extractBase64Files(item.taskDetails?.taskDescription),
      ]) || []),
    ];

    // Upload all images and get updated content with mediaIds
    const mediaIds = await uploadImagesFromContent(listOfImages, {
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      entityType: EntityType.QUESTION,
      createdBy: getUserId(),
    });

    // Collect all document files from all fields and convert to FileData format
    // Filter out empty placeholder files (size === 0) that were created for existing documents
    const allDocumentFiles = Object.values(documentFiles)
      .flat()
      .filter((file) => file.size > 0);
    const documentFileData = await convertFilesToFileData(allDocumentFiles);

    // Upload all documents and get their mediaIds
    const documentMediaIds = await uploadfiles(documentFileData, {
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      entityType: EntityType.QUESTION,
      createdBy: getUserId(),
    });

    const updatedContent = replaceAllImagesWithMediaIdsOfContents(formData, mediaIds);
    const { updatedFormData, mediaIdsMap } =
      replaceAllImageUrlsWithMediaIdsOfContents(updatedContent);

    // Merge existing and new document mediaIds
    const finalMediaIdsMap = mergeMediaIdsFromDocuments(
      documentFilesMetadata,
      documentFiles,
      documentMediaIds,
      mediaIdsMap,
    );

    // Build mediaIdsMap for each subquestion
    const subQuestionsWithMediaIdsMap = updatedFormData.subQuestions?.map(
      (subQuestion, index) => {
        const subQuestionMediaIdsMap: Record<string, string[]> = {};

        // Extract embedded image mediaIds from instruction
        if (subQuestion.instruction) {
          const instructionImageIds = extractSrcValues(subQuestion.instruction);
          if (instructionImageIds.length > 0) {
            subQuestionMediaIdsMap.instruction = instructionImageIds;
          }
        }

        // Extract embedded image mediaIds from taskDescription
        if (subQuestion.taskDetails?.taskDescription) {
          const taskDescImageIds = extractSrcValues(
            subQuestion.taskDetails.taskDescription,
          );
          if (taskDescImageIds.length > 0) {
            subQuestionMediaIdsMap.taskDescription = taskDescImageIds;
          }
        }

        // Add document mediaIds from instruction field
        const instructionFieldKey = getSubQuestionFieldKey(index, 'instruction');
        const instructionDocMediaIds = finalMediaIdsMap[instructionFieldKey];
        if (instructionDocMediaIds && Array.isArray(instructionDocMediaIds)) {
          subQuestionMediaIdsMap.instruction = [
            ...(subQuestionMediaIdsMap.instruction || []),
            ...instructionDocMediaIds,
          ];
        }

        // Add document mediaIds from taskDescription field
        const taskDescriptionFieldKey = getSubQuestionFieldKey(index, 'taskDescription');
        const taskDescDocMediaIds = finalMediaIdsMap[taskDescriptionFieldKey];
        if (taskDescDocMediaIds && Array.isArray(taskDescDocMediaIds)) {
          subQuestionMediaIdsMap.taskDescription = [
            ...(subQuestionMediaIdsMap.taskDescription || []),
            ...taskDescDocMediaIds,
          ];
        }

        // Destructure to exclude legacy mediaIds field from save payload
        const { mediaIds, ...subQuestionWithoutMediaIds } = subQuestion;

        return {
          ...subQuestionWithoutMediaIds,
          mediaIdsMap: subQuestionMediaIdsMap,
        };
      },
    );

    const taskDetails = updatedFormData.taskName
      ? {
          taskName: updatedFormData.taskName,
          taskDescription: updatedFormData.taskDescription,
          taskDurationDays: updatedFormData.taskDurationDays,
        }
      : undefined;

    // Common data preparation
    const baseData = {
      programId: getUserProgramId(),
      divisionName: updatedFormData.divisionName,
      businessUnit: updatedFormData.businessUnit,
      auditScore: Number(updatedFormData.auditScore),
      isActive: updatedFormData.isActive,
      defaultLanguage: updatedFormData.defaultLanguage.value!,
      defaultLanguageContent: {
        questionDetails: {
          title: updatedFormData.title,
          questionText: updatedFormData.questionText,
          auditInstruction: updatedFormData.auditInstruction,
          passCriteria: updatedFormData.passCriteria,
          failCriteria: updatedFormData.failCriteria,
          justification: updatedFormData.justification,
        },
        taskDetails: taskDetails,
        subQuestions: subQuestionsWithMediaIdsMap,
      },
      categories: updatedFormData.categories,
      docLinks: updatedFormData.docLinks,
      locationsExcluded: updatedFormData.locationsExcluded,
      tagsIncluded: updatedFormData.tagsIncluded,
      tagsExcluded: updatedFormData.tagsExcluded,
      mediaIdsMap: finalMediaIdsMap,
      requireCommentOnFail: updatedFormData.requireCommentOnFail,
      requireEvidenceOnFail: updatedFormData.requireEvidenceOnFail,
      requireCommentOnPass: updatedFormData.requireCommentOnPass,
      requireEvidenceOnPass: updatedFormData.requireEvidenceOnPass,
    };

    if (!question) {
      // For create operation
      createQuestion({
        ...baseData,
        createdBy: getUserId(),
      });
    } else if (isCopyQuestion) {
      // For copy operation,
      createQuestion({
        ...baseData,
        createdBy: getUserId(),
      });
    } else {
      // For update operation
      updateQuestion({
        ...baseData,
        questionId: question.questionId,
        version: question.version, // Include version for optimistic locking
        updatedBy: getUserId(),
        reason: updatedFormData.changeLog || '',
        changeLog: [
          ...(question.changeLog || []),
          {
            changedBy: getUserId(),
            reason: updatedFormData.changeLog,
            changedAt: Date.now().toString(),
            version: question.version + 1,
            changedFields: [],
          },
        ],
      });
    }
  };

  // Handle wizard submit: validate first, then show English confirmation modal
  const handleSubmit = () => {
    if (!validate()) {
      setSaveClicked(true);
      return;
    }
    setShowEnglishConfirmationModal(true);
  };

  const handleEnglishConfirmationConfirm = () => {
    setShowEnglishConfirmationModal(false);
    void performSubmit();
  };

  return (
    <>
      <UploadProgressIndicator
        data-testid="upload-progress-indicator"
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />
      <ApiStatusFlashBar
        data-testid="api-status-flash-bar"
        apiStatus={[
          ...validationStatus,
          createQuestionApiStatus,
          updateQuestionApiStatus,
        ]}
        onRefresh={handleVersionConflictRefresh}
        clearMessages={() => {
          clearValidationMessages();
          clearQuestionAllApiStatus();
          setSaveClicked(false);
        }}
      />
      <Modal
        data-testid="english-confirmation-modal"
        visible={showEnglishConfirmationModal}
        onDismiss={() => setShowEnglishConfirmationModal(false)}
        header="Confirm content language"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => setShowEnglishConfirmationModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleEnglishConfirmationConfirm}>
                Confirm
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Alert type="warning">
          Have you confirmed that all content was provided in English?
        </Alert>
      </Modal>
      <Wizard
        data-testid="question-form-wizard"
        className="questionsForm"
        i18nStrings={{
          stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
          collapsedStepsLabel: (stepNumber, stepsCount) =>
            `Step ${stepNumber} of ${stepsCount}`,
          skipToButtonLabel: (step) => `Skip to ${step.title}`,
          navigationAriaLabel: 'Steps',
          cancelButton: 'Cancel',
          previousButton: 'Previous',
          nextButton: 'Next',
          submitButton: 'Save Question',
          optional: '',
        }}
        onNavigate={({ detail }) => handleStepChange(detail.requestedStepIndex)}
        onCancel={onCancel}
        onSubmit={handleSubmit}
        activeStepIndex={currentStep}
        allowSkipTo
        isLoadingNextStep={
          updateQuestionApiStatus.isLoading ||
          createQuestionApiStatus.isLoading ||
          isUploading
        }
        steps={steps.map((step) => {
          const stepObj: any = {
            title: step.title,
            content: step.content,
            isOptional: true,
          };

          // Only add description if it exists
          if ('description' in step && step.description) {
            stepObj.description = step.description;
          }

          return stepObj;
        })}
      />
    </>
  );
};

export default QuestionFormWizard;
